import { Button, Dialog } from '@aecom-ai-engineering/react-components';
import { useUpdateProjectMutation } from '@consigli/hooks';
import { type Project } from '@consigli/types';
import { Trash2 } from 'lucide-react';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Route } from '@/routes';

const PANEL =
  'flex flex-col gap-[var(--ds-space-4)] rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-secondary)] p-[var(--ds-space-6)]';

interface AdminControlsPanelProps {
  project: Project;
}

/**
 * The frame's Admin Settings section: the one destructive action on the tab.
 *
 * **Delete here is a soft delete.** The button, the panel and the confirmation
 * all say "delete", and what goes to the API is `isHidden: true` — the project
 * drops out of every list and nobody on the team reaches it, but the row and
 * everything hanging off it survive and an operator can restore it. This is
 * deliberate, and it is what the project management list has always meant by
 * the word.
 *
 * `DELETE /projects/{id}/` is therefore never called from this tab. The
 * endpoint and `useDeleteProjectMutation` still exist and backoffice still
 * calls them — this is the one place that chose not to.
 *
 * The consequence to know about: the copy says the project cannot be restored
 * and it can. The error runs in the safe direction — someone reads it as final
 * and treats the decision as final — but anyone answering a "we deleted it by
 * mistake" request should look for the project rather than for a backup.
 *
 * Rendered only for someone who may manage the project — see
 * `useCanManageProject`. The caller decides; this does not re-check.
 */
export const AdminControlsPanel: FC<AdminControlsPanelProps> = ({ project }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [updateProject, { isLoading: isDeleting }] = useUpdateProjectMutation();
  const [isConfirming, setIsConfirming] = useState(false);

  const onDelete = useCallback(async () => {
    try {
      await updateProject({ projectId: project.id, data: { isHidden: true } }).unwrap();
      toast.success(t('project-overview.settings-project-deleted'));
      // The project is gone from the list this route is built around, so
      // staying would leave the tab describing something unreachable.
      void navigate(`/${Route.PROJECTS}`);
    } catch {
      toast.error(t('project-overview.settings-project-delete-failed'));
    } finally {
      setIsConfirming(false);
    }
  }, [navigate, project.id, t, updateProject]);

  const deleteLabel = (
    <>
      <Trash2 aria-hidden="true" />
      {t('project-overview.settings-delete-action')}
    </>
  );

  return (
    <div className="flex flex-col gap-[var(--ds-space-4)]">
      {/* The frame's heading: 18/22 at weight 500, and no icon — an earlier
          version set this beside a shield, and beside that in a filled chip. */}
      <h3 className="text-lg leading-[22px] font-medium text-[var(--ds-color-text-primary)]">
        {t('project-overview.settings-admin-heading')}
      </h3>

      {/* The frame's panel: a title, a line of consequence, and one button. */}
      <section className={PANEL}>
        <div className="flex flex-col gap-1">
          <h3 className="text-base leading-6 font-medium text-[var(--ds-color-text-primary)]">
            {t('project-overview.settings-delete-title')}
          </h3>
          <p className="text-[14px] leading-5 text-[color:var(--ds-color-raw-stone-600)]">
            {t('project-overview.settings-delete-help')}
          </p>
        </div>

        <div>
          <Button onClick={() => setIsConfirming(true)} size="md" variant="error">
            {deleteLabel}
          </Button>
        </div>
      </section>

      <Dialog
        data-confirm-dialog=""
        onOpenChange={({ open }) => !open && setIsConfirming(false)}
        open={isConfirming}
        primaryAction={{
          disabled: isDeleting,
          label: deleteLabel,
          onClick: () => void onDelete(),
        }}
        primaryActionVariant="error"
        secondaryAction={{
          label: t('project-overview.settings-cancel'),
          onClick: () => setIsConfirming(false),
        }}
        secondaryActionVariant="outline"
        title={t('project-overview.settings-delete-title')}
      >
        {t('project-overview.settings-delete-confirm-body', { name: project.name })}
      </Dialog>
    </div>
  );
};
